import {
    Card,
    CardBody,
    Typography,
    Tooltip,
    Input,
    Checkbox,
    Avatar,
    Progress,
    Badge
} from "@material-tailwind/react";
import {
    QuestionMarkCircleIcon,
    ArrowLongUpIcon,
    ArrowLongDownIcon,
    MagnifyingGlassIcon
} from "@heroicons/react/24/outline";
import React, {useEffect, useMemo, useState} from "react";
import getData from "../../api/axiosInstance";
import {useTheme} from "../common/ThemeContext";

export default function ItemSalesSpeedCard({ fixedName = false }) {
    const [itemSaleSpeedData, setItemSaleSpeedData] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'combinedAverageDays',
        direction: 'ascending'
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [showSoldOnly, setShowSoldOnly] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { isDark } = useTheme();

    const token = localStorage.getItem("token");

    const headers = useMemo(() => {
        return {
            'Authorization': `Bearer ${token}`
        };
    }, [token]);

    useEffect(() => {
        setIsLoading(true);
        const endpoint = showSoldOnly
            ? "/purchased_items/item_sales_speed"
            : "/purchased_items/item_sales_speed_with_available";

        getData(
            endpoint,
            headers,
            (data) => {
                // Filter out items with 0.0 days sales speed
                const filteredData = data.filter(manga => {
                    const days = showSoldOnly ? manga.averageDaysToSell : manga.combinedAverageDays;
                    return days !== null && !isNaN(days) && days > 0;
                });
                setItemSaleSpeedData(filteredData);
                setIsLoading(false);
            },
            "Error fetching item sales speed data"
        );
    }, [headers, showSoldOnly]);

    const parseTitle = (title) => {
        return title
            .toLowerCase()
            .split(/(\d+)/)
            .map(part => isNaN(part) ? part : parseInt(part, 10));
    };

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const filteredItems = itemSaleSpeedData.filter(manga => {
        const queryWords = searchQuery.toLowerCase().split(' ').filter(Boolean);
        const matchesSearch = queryWords.every(word =>
            manga.title.toLowerCase().includes(word)
        );
        return matchesSearch;
    });

    const sortedItemSalesSpeedData = sortConfig
        ? [...filteredItems].sort((a, b) => {
            // Handle numeric sorting for days
            if (sortConfig.key === 'combinedAverageDays' || sortConfig.key === 'averageDaysToSell') {
                const aValue = (showSoldOnly ? a.averageDaysToSell : a.combinedAverageDays) || 0;
                const bValue = (showSoldOnly ? b.averageDaysToSell : b.combinedAverageDays) || 0;

                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            }

            // Handle title sorting
            if (sortConfig.key === 'title') {
                const parsedA = parseTitle(a.title);
                const parsedB = parseTitle(b.title);
                for (let i = 0; i < Math.max(parsedA.length, parsedB.length); i++) {
                    if (parsedA[i] < parsedB[i]) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (parsedA[i] > parsedB[i]) return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            }

            // Default numeric sorting for counts
            const aValue = a[sortConfig.key] || 0;
            const bValue = b[sortConfig.key] || 0;

            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        })
        : filteredItems;

    const getSpeedColor = (days) => {
        if (!days) return 'gray';
        if (days < 30) return 'green';
        if (days < 60) return 'amber';
        return 'red';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
    };


    return (
        <Card className="w-full shadow-[0_0_15px_rgba(0,0,0,0.1)] relative overflow-hidden dark:bg-dark-900">
            <CardBody className="relative">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                        <Typography variant="h5" color="blue-gray" className="dark:text-gray-300">
                            Item Sales Performance
                        </Typography>
                        <Tooltip content="Shows average days to sell. Includes both sold items and eligible items (in stock >30 days)">
                            <QuestionMarkCircleIcon className="h-5 w-5 cursor-pointer text-cyan-800 dark:text-cyan-200" />
                        </Tooltip>
                    </div>
                    <Checkbox
                        label={
                            <Typography color="blue-gray" className="flex text-sm text-gray-600 dark:text-gray-400">
                                Show Sold Only
                            </Typography>
                        }
                        color="cyan"
                        ripple={false}
                        className="h-5 w-5 dark:border-gray-400 dark:checked:bg-cyan-600 dark:checked:border-cyan-600"
                        checked={showSoldOnly}
                        onChange={(e) => setShowSoldOnly(e.target.checked)}
                    />
                </div>
                <hr className="my-2 border-blue-gray-50 dark:border-gray-700" />

                {isLoading ? (
                    <tbody className="bg-white dark:bg-dark-900 h-[calc(100vh-24rem)]">
                    <tr>
                        <td colSpan={20} className="px-6 py-4">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="flex flex-col items-center justify-center space-y-4">
                                    <div className="relative w-24 h-24">
                                        <img
                                            src="/images/arcado-logo-solo-circle.png"
                                            alt="Arcado Logo"
                                            className="w-full h-full object-contain animate-[pulse_1.5s_ease-in-out_infinite]"
                                        />
                                        <div className="absolute inset-0 rounded-full border-4 border-pink-500 border-opacity-30 animate-[ping_1.5s_ease-in-out_infinite]"></div>
                                    </div>

                                    <div className="text-center">
                                        <Typography variant="h6" className="text-gray-800 font-bold">
                                            Loading sales data...
                                        </Typography>
                                        <Typography variant="small" className="text-gray-500 mt-1">
                                            We're preparing something awesome for you!
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                ) : (
                    <div
                        className={`relative overflow-x-auto h-[calc(100vh-24rem)] ${
                            isLoading ? 'overflow-x-hidden' : ''
                        } scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200
                        dark:scrollbar-thumb-gray-400 dark:scrollbar-track-dark-800`}
                    >
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="sticky top-0 bg-gray-50 dark:bg-dark-800 z-10">
                            <tr>
                                <th
                                    onClick={() => handleSort('title')}
                                    className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[180px]"
                                >
                                    Item
                                    {sortConfig?.key === 'title' && (
                                        sortConfig.direction === 'ascending'
                                            ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800" />
                                            : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800" />
                                    )}
                                </th>
                                <th
                                    onClick={() => handleSort(showSoldOnly ? 'averageDaysToSell' : 'combinedAverageDays')}
                                    className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 cursor-pointer min-w-[150px]"
                                >
                                    Sales Speed
                                    {sortConfig?.key === (showSoldOnly ? 'averageDaysToSell' : 'combinedAverageDays') && (
                                        sortConfig.direction === 'ascending'
                                            ? <ArrowLongUpIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800" />
                                            : <ArrowLongDownIcon strokeWidth={2} className="w-4 h-4 inline ml-2 text-cyan-800" />
                                    )}
                                </th>
                                <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 hover:text-cyan-900 dark:hover:text-cyan-300 dark:bg-dark-800 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 min-w-[120px]">
                                    Inventory
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-dark-900 divide-y divide-gray-300 dark:divide-gray-700">
                            {sortedItemSalesSpeedData.map((manga) => (
                                <tr key={`${manga.title}-${manga.earliestAvailabilityDate}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <Avatar
                                                src={manga.imageUrl}
                                                alt={manga.title}
                                                className="rounded-lg border border-gray-200"
                                            />
                                            <div className="ml-4">
                                                <Typography variant="small" className="font-medium text-gray-900 dark:text-gray-300">
                                                    {manga.title}
                                                </Typography>
                                                <Typography variant="small" className="text-gray-500">
                                                    Added: {formatDate(manga.earliestAvailabilityDate)}
                                                </Typography>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-24">
                                                <Progress
                                                    value={Math.min(
                                                        (showSoldOnly ? manga.averageDaysToSell : manga.combinedAverageDays) || 0,
                                                        100
                                                    )}
                                                    color={getSpeedColor(
                                                        showSoldOnly ? manga.averageDaysToSell : manga.combinedAverageDays
                                                    )}
                                                    className="h-2 dark:[&::-webkit-progress-bar]:bg-gray-700 dark:[&::-webkit-progress-value]:bg-opacity-80"
                                                />
                                            </div>
                                            <Typography variant="small" className={`font-medium ${
                                                getSpeedColor(showSoldOnly ? manga.averageDaysToSell : manga.combinedAverageDays) === 'green' ?
                                                    'text-green-600 dark:text-green-400' :
                                                    getSpeedColor(showSoldOnly ? manga.averageDaysToSell : manga.combinedAverageDays) === 'amber' ?
                                                        'text-amber-600 dark:text-amber-400' :
                                                        'text-red-600 dark:text-red-400'
                                            }`}>
                                                {((showSoldOnly ? manga.averageDaysToSell : manga.combinedAverageDays) || 0).toFixed(1)} days
                                            </Typography>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex flex-col">
                                                {/* Sold count */}
                                                <div className="flex items-center">
                                                    <div className="w-6 h-6 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center mr-2">
                                                        <span className="text-xs font-medium text-green-800 dark:text-green-300">
                                                            {manga.soldCount}
                                                        </span>
                                                    </div>
                                                    <Typography variant="small" className="text-gray-600 dark:text-gray-300">
                                                        Sold
                                                    </Typography>
                                                </div>

                                                {!showSoldOnly && (
                                                    <div className="flex items-center mt-1">
                                                        <div className="w-6 h-6 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center mr-2">
                                                            <span className="text-xs font-medium text-amber-800 dark:text-amber-300">
                                                                {manga.availableCount}
                                                            </span>
                                                        </div>
                                                        <Typography variant="small" className="text-gray-600 dark:text-gray-300">
                                                            Available
                                                        </Typography>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="flex items-center mt-5 w-full gap-2 md:w-max">
                    <div className="relative w-full">
                        <Input
                            type="search"
                            placeholder="Search items..."
                            containerProps={{
                                className: "min-w-full sm:min-w-[288px] dark:border-gray-600 dark:focus-within:border-gray-500",
                            }}
                            className="!border-t-blue-gray-300 pl-9 placeholder:text-blue-gray-300 focus:!border-blue-gray-300 dark:bg-dark-800 dark:text-white dark:placeholder:text-gray-400 dark:border-gray-600 dark:focus:border-gray-500"
                            labelProps={{
                                className: "before:content-none after:content-none",
                            }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={
                                <MagnifyingGlassIcon className="h-5 w-5 text-blue-gray-300 dark:text-gray-400" />
                            }
                            color={isDark ? "white" : "gray"}
                        />
                    </div>
                </div>
            </CardBody>
        </Card>
    );
}
